import { api } from "../api";
import {
  Exercise,
  ExerciseContainer,
  ExerciseContainerFromApi,
  ExerciseRequest,
  ExercisesResponse,
} from "./types";

const extendedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllExercises: builder.query<Exercise[], void>({
      query: () => ({
        url: "/exercise",
        method: "GET",
      }),
      transformResponse: transformExercises,
      providesTags: ["Exercises"],
    }),
    createExercise: builder.mutation<ExercisesResponse, Omit<Exercise, "id">>({
      query: (exerciseReq) => ({
        url: "/exercise/",
        method: "POST",
        body: transformExerciseRequest(exerciseReq),
      }),
      invalidatesTags: ["Exercises"],
    }),
    updateExercise: builder.mutation<ExercisesResponse, Exercise>({
      query: (exerciseReq) => ({
        url: "/exercise/",
        method: "PUT",
        body: transformExerciseRequest(exerciseReq),
      }),
      invalidatesTags: ["Exercises", "Workout"],
    }),
    deleteExercise: builder.mutation<ExercisesResponse, string>({
      query: (exerciseId) => ({
        url: "/exercise/" + exerciseId,
        method: "DELETE",
      }),
      invalidatesTags: ["Exercises"],
    }),

    // --- Exercise Container endpoints ---
    getAllExerciseContainers: builder.query<ExerciseContainer[], void>({
      query: () => ({ url: "/exercise-container", method: "GET" }),
      transformResponse: (response: ExerciseContainerFromApi[]) =>
        response.map((c) => ({
          id: c.id,
          name: c.name,
          color: c.color,
          parentId: c.parentId ?? null,
        })),
      providesTags: ["ExerciseContainers"],
    }),
    createExerciseContainer: builder.mutation<
      ExerciseContainer,
      { name: string; color?: string; parentId?: number | null }
    >({
      query: (container) => ({
        url: "/exercise-container",
        method: "POST",
        body: container,
      }),
      transformResponse: (response: ExerciseContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
        parentId: response.parentId ?? null,
      }),
      invalidatesTags: ["ExerciseContainers"],
    }),
    updateExerciseContainer: builder.mutation<
      ExerciseContainer,
      ExerciseContainer
    >({
      query: (container) => ({
        url: "/exercise-container",
        method: "PUT",
        body: container,
      }),
      transformResponse: (response: ExerciseContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
        parentId: response.parentId ?? null,
      }),
      invalidatesTags: ["ExerciseContainers"],
    }),
    deleteExerciseContainer: builder.mutation<
      void,
      { id: number; deleteExercises: boolean }
    >({
      query: ({ id, deleteExercises }) => ({
        url: `/exercise-container/${id}?deleteExercises=${deleteExercises}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExerciseContainers", "Exercises"],
    }),
    moveExerciseToContainer: builder.mutation<
      void,
      { id: string | number; idContainer: number | null }
    >({
      query: ({ id, idContainer }) => ({
        url:
          `/exercise/${id}/container` +
          (idContainer != null ? `?idContainer=${idContainer}` : ""),
        method: "PUT",
      }),
      invalidatesTags: ["Exercises"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllExercisesQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  useGetAllExerciseContainersQuery,
  useCreateExerciseContainerMutation,
  useUpdateExerciseContainerMutation,
  useDeleteExerciseContainerMutation,
  useMoveExerciseToContainerMutation,
} = extendedApi;

function transformExercises(response: ExercisesResponse): Exercise[] {
  return response.map((exercise) => {
    let tags: string[] = [];
    let videoUrls: string[] = [];
    let muscleDistribution: { district: string; percentage: number }[] | undefined;
    let specialCategoryId: number | undefined;
    let checkDistrict: string | undefined;
    let faticaSistemica: string | undefined;
    let profiloResistenza: string | undefined;
    try {
      const parsed = JSON.parse(exercise.exercisesData);
      tags = parsed?.tags || [];
      videoUrls = parsed?.videoUrls || [];
      muscleDistribution = parsed?.muscleDistribution;
      specialCategoryId = parsed?.specialCategoryId;
      checkDistrict = parsed?.checkDistrict;
      faticaSistemica = parsed?.faticaSistemica;
      profiloResistenza = parsed?.profiloResistenza;
    } catch (e) {
      console.error(e);
    }
    return {
      ...exercise,
      notes: exercise.note,
      tags,
      videoUrls,
      muscleDistribution,
      specialCategoryId,
      checkDistrict,
      faticaSistemica,
      profiloResistenza,
      name: exercise.desc || "",
      id: exercise.id + "",
      idContainer: exercise.idContainer ?? null,
    };
  });
}

function transformExerciseRequest(
  exercise: Exercise | Omit<Exercise, "id">
): ExerciseRequest {
  const { name, tags, videoUrls, notes, muscleDistribution, specialCategoryId, checkDistrict, faticaSistemica, profiloResistenza, idContainer, ...rest } = exercise;
  const exercisesData = JSON.stringify({
    tags: exercise.tags || [],
    videoUrls: exercise.videoUrls || [],
    ...(muscleDistribution?.length ? { muscleDistribution } : {}),
    ...(specialCategoryId != null ? { specialCategoryId } : {}),
    ...(checkDistrict ? { checkDistrict } : {}),
    ...(faticaSistemica ? { faticaSistemica } : {}),
    ...(profiloResistenza ? { profiloResistenza } : {}),
  });

  return {
    desc: exercise.name,
    exercisesData,
    link: "",
    note: notes,
    sets: [],
    // @ts-ignore
    id: exercise.id ? parseInt(exercise.id) : null,
    ...rest,
    type: "DEFAULT",
  };
}
